import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/Bits";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Send, Copy, Slack } from "lucide-react";

export default function SlackPanel({ tenant }) {
  const tenantId = tenant?.id;
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["slack", tenantId], queryFn: () => api.get(`/slack/${tenantId}`).then((r) => r.data), enabled: !!tenantId });
  const deliveries = useQuery({ queryKey: ["slack-deliveries", tenantId], queryFn: () => api.get(`/slack/${tenantId}/deliveries`).then((r) => r.data), enabled: !!tenantId, refetchInterval: 15000 });
  const [form, setForm] = useState({ enabled: false, mode: "bot", bot_token: "", channel: "", webhook_url: "", signing_secret: "" });
  useEffect(() => { if (cfg.data) setForm({ ...form, ...cfg.data, bot_token: cfg.data.bot_token || "", webhook_url: cfg.data.webhook_url || "", channel: cfg.data.channel || "", signing_secret: "" }); }, [cfg.data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: () => api.put(`/slack/${tenantId}`, { ...form, console_url: window.location.origin }).then((r) => r.data),
    onSuccess: (d) => { qc.setQueryData(["slack", tenantId], d); toast.success("Slack settings saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const test = useMutation({
    mutationFn: () => api.post(`/slack/${tenantId}/test`).then((r) => r.data),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["slack-deliveries", tenantId] }); d?.status === "ok" ? toast.success("Test alert delivered to Slack") : toast.error(`Slack: ${d?.status}${d?.error ? " · " + d.error : ""}`); },
  });

  const interactUrl = `${window.location.origin}/api/slack/interact`;
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const inputCls = "w-full bg-[#1B1B28] border hairline px-3 py-2 text-xs font-mono focus:outline-none focus:border-cyan-400/60";

  return (
    <Panel kicker={`Slack Alerts · ${tenant?.name || ""}`} title="Auto-isolations & approvals in Slack"
      right={<Switch data-testid="slack-enabled" checked={!!form.enabled} onCheckedChange={set("enabled")} />}>
      <div className="p-4 space-y-3 text-sm">
        <div data-testid="slack-mode" className="flex border hairline w-fit">
          {[["bot", "Bot token · interactive buttons"], ["webhook", "Incoming webhook · link buttons"]].map(([m, l]) => (
            <button key={m} data-testid={`slack-mode-${m}`} onClick={() => setForm({ ...form, mode: m })}
              className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors duration-150 ${form.mode === m ? "bg-cyan-400 text-[#15151F]" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>{l}</button>
          ))}
        </div>
        {form.mode === "bot" ? (
          <>
            <Field label="Bot User OAuth Token (xoxb-…) · scope chat:write"><input data-testid="slack-bot-token" className={inputCls} value={form.bot_token} onChange={set("bot_token")} placeholder="xoxb-…" /></Field>
            <Field label="Channel ID or #name (invite the bot to it)"><input data-testid="slack-channel" className={inputCls} value={form.channel} onChange={set("channel")} placeholder="C0123456789 or #soc-alerts" /></Field>
            <Field label={`Signing Secret (Basic Information) ${cfg.data?.signing_secret_set ? "· set" : ""}`}><input data-testid="slack-signing-secret" type="password" className={inputCls} value={form.signing_secret} onChange={set("signing_secret")} placeholder={cfg.data?.signing_secret_set ? "•••••••• (leave blank to keep)" : "paste to enable Approve/Reject buttons"} /></Field>
            <div className="border border-cyan-400/30 bg-cyan-400/[0.03] p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 mb-1">Slack app → Interactivity & Shortcuts → Request URL</div>
              <div className="flex items-center gap-2">
                <code data-testid="slack-interact-url" className="font-mono text-[11px] text-zinc-100 break-all flex-1">{interactUrl}</code>
                <button data-testid="copy-interact-url" onClick={() => navigator.clipboard.writeText(interactUrl).then(() => toast.success("Copied"))} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
              </div>
            </div>
          </>
        ) : (
          <Field label="Incoming Webhook URL"><input data-testid="slack-webhook-url" className={inputCls} value={form.webhook_url} onChange={set("webhook_url")} placeholder="https://hooks.slack.com/services/…" /></Field>
        )}
        <div className="flex gap-2">
          <button data-testid="slack-save-btn" onClick={() => save.mutate()} disabled={save.isPending}
            className="flex-1 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-150"><Slack size={13} /> Save</button>
          <button data-testid="slack-test-btn" onClick={() => test.mutate()} disabled={test.isPending || !cfg.data?.enabled}
            className="flex-1 border hairline text-zinc-300 hover:bg-white/5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 transition-colors duration-150"><Send size={13} /> Send test alert</button>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Delivery log</div>
          <div data-testid="slack-deliveries" className="border hairline divide-y divide-white/5 max-h-40 overflow-y-auto">
            {(deliveries.data || []).map((d) => (
              <div key={d.id} className="px-3 py-1.5 text-[11px] flex items-center gap-2">
                <span className={`font-mono text-[9px] uppercase tracking-widest w-24 shrink-0 ${d.status === "ok" ? "text-emerald-400" : d.status === "error" ? "text-red-400" : "text-orange-400"}`}>{d.status.replace("_", " ")}</span>
                <span className="text-zinc-300 truncate flex-1">{d.kind.replace("_", " ")} · {d.title}{d.error ? ` · ${d.error}` : ""}</span>
                <span className="font-mono text-[9px] text-zinc-600 shrink-0">{new Date(d.ts).toLocaleTimeString()}</span>
              </div>
            ))}
            {(deliveries.data || []).length === 0 && <div className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-zinc-600">No deliveries yet</div>}
          </div>
        </div>
      </div>
    </Panel>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    {children}
  </label>
);
